import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  DEFAULT_DATE_FORMAT_FNS,
  FilterKeys,
  Justification,
  JustificationCounts,
  JustificationStatus,
  useGetAttendanceCodesQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { usePrevious } from '@schooly/hooks/use-previous';
import {
  CrossIcon,
  DropdownYears,
  EmptyFolderSvg,
  ExpandableSearch,
  GridRowCell,
  GridRowItem,
  GridRowName,
  GridRowStyled,
  ModalContent,
  ModalHeader,
  ModalPanel,
  randomInt,
  TabsWithCounters,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import isEqual from 'lodash.isequal';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { AccessDenied } from '@schooly/style';
import { ModalMain } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { useAttendanceJustifications } from '../../../context/attendance/useAttendanceJustifications';
import { allowRenderStudents } from '../../../helpers/renderRoutesByPermissions';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { getCodesById } from '../attendance-utils';
import { AttendanceSidebar } from '../AttendanceTable/AttendanceSidebar/AttendanceSidebar';
import { useAttendanceSidebar } from '../AttendanceTable/AttendanceSidebar/useAttendanceSidebar';
import { canViewJustifications } from '../permissions';
import { AttendanceJustificationsTable } from './AttendanceJustificationsTable';
import { getJustificationSidebarSelection } from './utils';

const SKELETON_TABLE_COUNT = 3;

const JUSTIFICATION_TABS = [
  JustificationStatus.Pending,
  JustificationStatus.Submitted,
  JustificationStatus.Expired,
  JustificationStatus.Closed,
];

const getTabs = (counts?: JustificationCounts) => {
  return JUSTIFICATION_TABS.map((value) => ({
    labelId: `attendance-justification-Status-${value}`,
    value,
    count: counts?.[value] ?? 0,
  }));
};

type AttendanceJustificationsContentProps = {
  onClose: () => void;
};

export const AttendanceJustificationsContent: FC<AttendanceJustificationsContentProps> = ({
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '', permissions } = useAuth();
  const { defaultValidity, schoolYears } = useSchoolYears();
  const { data: codes } = useGetAttendanceCodesQuery(schoolId);
  const canView = canViewJustifications(permissions);
  const canViewStudents = allowRenderStudents(permissions);
  const { selected, openForCell, close } = useAttendanceSidebar();

  const {
    data,
    params,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    justifications,
    updatingIds,
    handleStatusChange,
    handleQueryChange,
    handleYearChange,
    handleUpdateEntryCode,
  } = useAttendanceJustifications({
    queryOptions: {
      onSuccess: (data) => {
        setTabs(getTabs(data.pages[0]?.counts));
      },
    },
  });

  const [tabs, setTabs] = useState(getTabs(data?.pages[0]?.counts));

  const codesById = useMemo(() => getCodesById(codes ?? []), [codes]);

  const handleOpenSidebar = useCallback(
    (justification: Justification) => {
      const selection = getJustificationSidebarSelection(justification);

      if (selection) openForCell(selection.data, selection.student);
    },
    [openForCell],
  );

  const justificationsByDate = useMemo(() => {
    return justifications.reduce<Record<string, Justification[]>>((prev, curr) => {
      const entryDate = curr.attendance_entries[0]?.register.date;
      if (!entryDate) return prev;

      const date = format(newDateTimezoneOffset(entryDate), DEFAULT_DATE_FORMAT_FNS);
      if (!prev[date]) prev[date] = [];
      prev[date].push(curr);
      return prev;
    }, {});
  }, [justifications]);

  const selectedSchoolYear = useMemo(
    () =>
      schoolYears.find(
        (y) =>
          y.start === params.filters?.[FilterKeys.Date]?.[0] &&
          y.end === params.filters?.[FilterKeys.Date]?.[1],
      ),
    [params.filters, schoolYears],
  );

  const prevParams = usePrevious(params);
  const statusChanged =
    !!prevParams && !isEqual(prevParams?.filters?.statuses, params.filters?.statuses);
  const isTabsLoading = !data && isLoading && !statusChanged;
  const accessDenied = !canView || error;
  const isEmpty = !justifications.length;

  const loaderRef = useInfiniteScroll(
    isLoading || isFetchingNextPage || !hasNextPage,
    fetchNextPage,
  );

  useEffect(() => {
    trackAmplitudeEvent({
      name: AnalyticsEventName.AttendanceJustificationsListView,
    });
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return <AttendanceJustificationsTableSkeleton count={SKELETON_TABLE_COUNT} />;
    }

    if (isEmpty) {
      return (
        <AttendanceJustificationsEmptyStub
          textId={
            params.query
              ? 'attendance-justification-NoJustificationsMatching'
              : 'attendance-justification-NoJustifications'
          }
        />
      );
    }

    return (
      <Stack gap={2.5}>
        {Object.entries(justificationsByDate).map(([date, justifications]) => (
          <AttendanceJustificationsTable
            key={date}
            justifications={justifications}
            codes={codes ?? []}
            date={date}
            schoolId={schoolId}
            updatingIds={updatingIds}
            canViewStudents={canViewStudents}
            onUpdateEntryCode={handleUpdateEntryCode}
            onOpenSidebar={handleOpenSidebar}
          />
        ))}
        {hasNextPage && (
          <>
            {isFetchingNextPage && (
              <AttendanceJustificationsTableSkeleton count={SKELETON_TABLE_COUNT} />
            )}
            <Box ref={loaderRef} />
          </>
        )}
      </Stack>
    );
  };

  const renderCloseButton = () => {
    return (
      <IconButton onClick={onClose}>
        <CrossIcon />
      </IconButton>
    );
  };

  if (accessDenied) {
    return (
      <>
        <ModalHeader withBorderBottom={false} active>
          {renderCloseButton()}
        </ModalHeader>
        <AccessDenied />
      </>
    );
  }

  return (
    <>
      <ModalHeaderV2
        withBorderBottom={false}
        withGoBack
        active
        title={
          <Typography variant="h1" pt={0.5}>
            {formatMessage({ id: 'attendance-justification-AbsenceJustifications' })}
          </Typography>
        }
        sx={{ paddingTop: 0, paddingBottom: 1.5 }}
      >
        {renderCloseButton()}
      </ModalHeaderV2>
      <ModalPanel withBorderBottom active sx={{ py: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" gap={2.5} alignItems="center">
          {isTabsLoading ? (
            <AttendanceJustificationsTabsSkeleton count={tabs.length} />
          ) : (
            <TabsWithCounters
              tabItems={tabs}
              onChange={(_, v) => handleStatusChange(v)}
              activeTab={params.filters?.statuses?.[0] ?? JustificationStatus.Pending}
              sx={{
                '& .MuiTabs-flexContainer': {
                  overflow: 'scroll',
                },
                '& .MuiTab-root': {
                  height: 24,
                  minHeight: 0,
                  '&:not(:last-child)': { mr: 4 },
                },
              }}
            />
          )}
          <Stack direction="row" gap={2.5} alignItems="center">
            <ExpandableSearch
              value={params.query ?? ''}
              onChange_MemoizedCallbackOnly={handleQueryChange}
              placeholder={formatMessage({ id: 'people-Search' })}
              withDebounce
            />
            <DropdownYears
              years={schoolYears}
              defaultYear={defaultValidity}
              currentYear={selectedSchoolYear}
              onYearChange={handleYearChange}
            />
          </Stack>
        </Stack>
      </ModalPanel>
      <ModalMain>
        <ModalContent>{renderContent()}</ModalContent>
      </ModalMain>

      {!!selected && (
        <AttendanceSidebar
          data={selected.data}
          student={selected.student}
          codesById={codesById}
          onClose={close}
        />
      )}
    </>
  );
};

const AttendanceJustificationsEmptyStub: FC<{ textId: string }> = ({ textId }) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={5.5} justifyContent="center" alignItems="center" position="relative" height="100%">
      <Box sx={{ svg: { width: 300 } }}>
        <EmptyFolderSvg />
      </Box>
      <Typography variant="h3" textAlign="center" color="text.primary">
        {formatMessage({ id: textId })}
      </Typography>
    </Stack>
  );
};

const AttendanceJustificationsTabsSkeleton: FC<{ count: number }> = ({ count }) => {
  return (
    <Stack direction="row" alignItems="center" gap={4}>
      {[...new Array(count)].map((_, i) => (
        <Typography variant="h3" key={i}>
          <Skeleton width={90} />
        </Typography>
      ))}
    </Stack>
  );
};

const AttendanceJustificationsTableSkeleton: FC<{ count: number }> = ({ count }) => {
  return (
    <Stack gap={2.5}>
      {Array.from({ length: count }).map((_, i) => (
        <Stack key={i}>
          {[...new Array(3)].map((_, i) => {
            const isHeader = i < 1;
            return (
              <GridRowStyled
                height={isHeader ? 34 : 44}
                sx={{ pointerEvents: 'none', overflow: 'hidden' }}
                key={i}
              >
                <GridRowItem gap={1} sx={{ height: '100%' }}>
                  <GridRowName minWidth={0} variant="body1">
                    <Skeleton width={`${randomInt(50, 100)}%`} />
                  </GridRowName>
                  {[...new Array(3)].map((_, i) => (
                    <GridRowCell minWidth={100} variant="body1" key={i}>
                      <Skeleton width={`${randomInt(50, 100)}%`} />
                    </GridRowCell>
                  ))}
                  <GridRowCell minWidth={50} variant="body1">
                    <Skeleton />
                  </GridRowCell>
                  <GridRowCell minWidth={200} variant="body1">
                    <Skeleton />
                  </GridRowCell>
                  <GridRowCell minWidth={20} variant="body1">
                    <Skeleton />
                  </GridRowCell>
                </GridRowItem>
              </GridRowStyled>
            );
          })}
        </Stack>
      ))}
    </Stack>
  );
};
